<template>
    <v-container fluid class="d-flex flex-column align-stretch flex-nowrap">
        <v-container fluid class="flex-grow-1 overflow-hidden pa-1">
            <v-row dense class="fill-height overflow-y-auto" justify="center">
                <v-col cols="12">
                    <span class="h6">{{ `${$t('labels.stepNo', { number: '1' })} - ${$t('labels.selectImportFile')}` }}</span>
                    <v-divider/>
                </v-col>
                <v-col cols="12" md="6" lg="3">
                    <uv-file-input v-model="uploadFile" show-drop accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" @change="handleFileChange"></uv-file-input>
                </v-col>
                <v-col cols="12">
                    <div class="d-flex align-center justify-space-between">
                        <span class="h6">{{ `${$t('labels.stepNo', { number: '2' })} - ${$t('labels.previewData')}` }}</span>
                        <div class="d-flex align-center">
                            <uv-button @click="downloadTemplate" :loading="templateDownloadBusy" small text outlined color="success">
                                <v-icon left>{{ icons.mdiDownload }}</v-icon>
                                <span>model</span>
                            </uv-button>
                            <uv-button @click="previewData(); getFields()" :disabled="preview.busy || !uploadFile" small icon>
                                <v-icon :class="{'rotating': preview.busy}">{{  icons.mdiSync  }}</v-icon>
                            </uv-button>
                        </div>
                    </div>
                    <v-divider/>
                </v-col>
                <v-col cols="12">
                    <uv-data-table :items="preview.items" :headers="headerItems" class="uv-data-table-header-style" fixed-header hide-default-header>
                        <template #header="{ props: { headers } }">
                            <thead>
                                <tr>
                                    <th v-for="header in headers" :key="header.name" class="import-header">
                                        <uv-select :items="fields.items.map(item => item.name)" :placeholder="header.text" clearable v-model="preview.headerMappings[header.value].name" 
                                            @change="handleFieldChange($event, preview.headerMappings[header.value])">
                                            <template #item="{ item }">
                                                {{ $t(`import.agent_visit.${item}`) }}
                                            </template>
                                            <template #selection="{ item }">
                                                {{ $t(`import.agent_visit.${item}`) }}
                                            </template>
                                        </uv-select>
                                    </th>
                                </tr>
                            </thead>
                        </template>
                    </uv-data-table>
                </v-col>
                <v-col cols="12">
                    <div class="h6">{{ `${$t('labels.stepNo', { number: '3' })} - ${$t('labels.uploadData')}` }}</div>
                    <div v-if="this.importData.items?.length > 0" class="h6 success--text">{{ $t('labels.uploadedEntriesNo', { number: this.importData.items.length}) }}</div>
                    <v-divider/>
                </v-col>
                <v-col cols="12" v-show="importData.errors?.length > 0">
                    <span class="h6 error--text">{{ $t('labels.errors') }}</span>
                    <uv-data-table :items="importData.errors" :headers="errorHeaders" class="uv-data-table-header-style" fixed-header>
                    </uv-data-table>
                </v-col>
            </v-row>
        </v-container>
        <div class="actions d-flex justify-end pt-1">
            <slot name="other-actions">
                <uv-button color="uv-cancel" text @click="handleCancel">{{ $t('cancel') }}</uv-button>
            </slot>
            <uv-button color="success" @click="saveImport" :loading="importData.busy">{{ $t('labels.upload') }}</uv-button>
        </div>
        <v-dialog v-model="enumDialog.display" max-width="500">
            <v-sheet class="pa-2">
                <uv-text-field v-for="item in Object.keys(enumDialog.data)" v-model="enumDialog.data[item]" :key="item" :label="item"></uv-text-field>
            </v-sheet>
        </v-dialog>
        <v-dialog v-model="boolDialog.display" max-width="500">
            <v-sheet class="pa-2">
                <uv-text-field v-model="boolDialog.data['true']" :label="$t('yes')"></uv-text-field>
                <uv-text-field v-model="boolDialog.data['false']" :label="$t('no')"></uv-text-field>
            </v-sheet>
        </v-dialog>
    </v-container>
</template>

<script>
import { mdiSync, mdiDownload } from "@mdi/js";
import { cleanupObject, downloadFileFromResponse } from "@/plugins/utils";
export default {
    name: "ActivitiesImport",
    props: {
        shouldReset: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uploadFile: null,
            preview: {
                items: [],
                headerMappings: {},
                busy: false,
                abortController: null
            },
            fields: {
                items: [],
                busy: false,
                abortController: null
            },
            importData: {
                items: [],
                errors: [],
                busy: false,
                abortController: null,
            },
            enumDialog: {
                display: false,
                data: {}
            },
            boolDialog: {
                display: false,
                data: {}
            },
            icons: {mdiSync, mdiDownload},
            templateDownloadBusy: false
        }
    },
    created() {
        this.getFields();
    },
    computed: {
        headerItems() {
            return Object.keys(this.preview.headerMappings).map((header) => ({
                value: header,
                text: header,
            }))
        },
        errorHeaders() {
            return this.fields.items.map((field) => ({
                value: field.name,
                text: this.$t(`import.agent_visit.${field.name}`),
            }))
        }
    },
    methods: {
        downloadTemplate() {
            this.templateDownloadBusy = true;
            this.$apiHttp({
                url: "/agent_visits/import/template",
                responseType: "blob",
            }).then((res) => {
                downloadFileFromResponse(res);
            })
            .finally(() => {
                this.templateDownloadBusy = false;
            }); 
        },
        previewData() {
            if (this.importData.busy) this.importData.abortController.abort();
            this.importData.busy = false;
            this.importData.items = [];
            this.importData.errors = [];

            if (this.preview.busy) this.abortController.abort();
            this.preview.busy = true;
            this.preview.abortController = new AbortController();
            let data = new FormData();
            data.append("file", this.uploadFile);
            this.$apiHttp({
                url: "agent_visits/import/preview",
                method: "post",
                data,
                signal: this.preview.abortController.signal
            }).then(res => {
                this.preview.items = res.data?.data.items;
                this.preview.headerMappings = res.data?.data.headers.reduce((obj, header) => ({...obj, [header]: { name: null }}), {});
            }).finally(() => this.preview.busy = false)
        },
        saveImport() {
            if (this.importData.busy) this.abortController.abort();
            this.importData.busy = true;
            this.importData.abortController = new AbortController();
            let data = new FormData();
            data.append("file", this.uploadFile);
            data.append("mappings", JSON.stringify(cleanupObject(this.preview.headerMappings)));
            this.$apiHttp({
                url: "agent_visits/import/data",
                method: "post",
                data,
                signal: this.importData.abortController.signal
            }).then(res => {
                this.importData.items = res.data?.data.import_data;
                this.importData.errors = res.data?.data.error_data;
            }).finally(() => this.importData.busy = false)
        },
        getFields() {
            if (this.fields.busy) this.abortController.abort();
            this.fields.busy = true;
            this.fields.abortController = new AbortController();
            this.$apiHttp({
                url: "agent_visits/import/fields",
                signal: this.fields.abortController.signal
            }).then(res => {
                this.fields.items = res.data?.data;
            }).finally(() => this.fields.busy = false)
        },
        reset() {
            this.uploadFile = null;
            if (this.preview.busy) this.preview.abortController.abort();
            this.preview.busy = false;
            this.preview.items = [];
            this.preview.headerMappings = {};
            
            if (this.fields.busy) this.fields.abortController.abort();
            this.fields.busy = false;
            this.fields.items = [];
            
            if (this.importData.busy) this.importData.abortController.abort();
            this.importData.busy = false;
            this.importData.items = [];
            this.importData.errors = [];
        },
        handleCancel(){
            this.$emit("cancel");
        },
        handleFieldChange(field, mapping) {
            let foundField = this.fields.items.find(item => item.name === field);
            mapping.field_type = foundField?.field_type
            if (foundField.field_type === "enum") {
                // enum logic
                mapping.values = foundField.values.reduce((obj, val) => ({...obj, [val]: null}), {})
                this.enumDialog.display = true;
                this.enumDialog.data = mapping.values;
            } else if (foundField.field_type === "bool") {
                foundField.values = { 
                    'true': null,
                    'false': null,
                };
                mapping.values = foundField.values;
                this.boolDialog.data = mapping.values;
                this.boolDialog.display = true;
            } else {
                mapping.values = null;
            }
        },
        handleFileChange(file) {
            this.uploadFile = file;
            
            if (this.preview.busy) this.preview.abortController.abort();
            this.preview.busy = false;
            this.preview.items = [];
            this.preview.headerMappings = {};
            
            if (this.fields.busy) this.fields.abortController.abort();
            this.fields.busy = false;
            this.fields.items = [];
            
            if (this.importData.busy) this.importData.abortController.abort();
            this.importData.busy = false;
            this.importData.items = [];
            this.importData.errors = [];
        }
    },
    watch: {
        shouldReset(newVal) {
            if (newVal) {
                this.reset();
                this.$emit("update:should-reset", false);
            }
        },
        "enumDialog.display"(newVal) {
            if (!newVal) {
                this.enumDialog.data = {};
            }
        },
        "boolDialog.display"(newVal) {
            if (!newVal) {
                this.boolDialog.data = {};
            }
        }
    }
}
</script>

<style lang="scss" scoped>

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
}

.rotating {
  animation: rotate 2s linear infinite;
}
.import-header {
    min-width: 100px
}
</style>